import './ScheduleTable.css'

import { compose, withProps } from 'recompose'
import React from 'react'
import { withRouter } from 'react-router-dom'
import muiThemeable from 'material-ui/styles/muiThemeable'

import _ from 'lodash'

import { makeMetaDialogProps } from './ScheduleTable'
import { withDialogs } from '../components/DialogProvider'
import DayCaption from '../components/DayCaption'
import {
  byDatesAsc,
  byTimesAsc,
  currentDate,
  formatLocalDateString,
  getDayName,
  toLocalDate,
  toLocalDateString,
  toLocalTimeString,
} from '../app/timeUtils'
import {
  getPreferredLocales,
  getProgramContent,
  getSessions,
} from '../app/selectors'
import content from '../content'
import ScheduleCard from '../components/ScheduleCard'
import { presentationDetailRoute } from '../app/routes'
import { isSessionCrossTrack, isTrackCrossTrack } from '../app/predicates'

const isTrackNonCrossTrack = _.negate(isTrackCrossTrack)

const SessionCard = withDialogs(
  withRouter(({ session, history, preferredLocales, openDialog }) => {
    const { presentation, meta } = session
    const endTime = session.endTime ? ` - ${session.endTime}` : ''
    const time = `${session.time}${endTime}`
    if (presentation) {
      return (
        <ScheduleCard
          id={presentation.id}
          title={presentation.title}
          subtitle={presentation.presenter.fullName}
          location={session.track.label}
          time={time}
          image={presentation.presenter.picture}
          imageAlt={presentation.presenter.fullName}
          color={session.track.color}
          onClick={() => history.push(presentationDetailRoute(presentation.id))}
        />
      )
    }
    if (meta) {
      return (
        <ScheduleCard
          id={meta.title}
          title={meta.title}
          location={session.track.label}
          time={time}
          image={meta.image}
          imageAlt={meta.title}
          color={session.track.color}
          onClick={() => openDialog(makeMetaDialogProps(session))}
        />
      )
    }
    return null
  })
)

class DayScheduleTable extends React.Component {
  cardRefs = {}

  render() {
    const { schedule, style, preferredLocales } = this.props
    const dateString = formatLocalDateString(schedule.date, preferredLocales)
    const dayName = getDayName(toLocalDate(schedule.date), preferredLocales)
    return (
      <div style={style}>
        <DayCaption dateString={dateString} dayName={dayName} />
        {schedule.timeRows.map(row => (
          <div key={row.time} ref={this.onRef(row)}>
            <div className="ScheduleTable-daySessionsContainer">
              {row.sessions
                .filter(s => !!s)
                .map((session, i) => (
                  <SessionCard
                    key={session.id}
                    session={session}
                    preferredLocales={preferredLocales}
                  />
                ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  onRef = row => ref => {
    this.cardRefs[row.time] = { row, ref }
  }

  scrollToTime(time) {
    const wrapper = this.cardRefs[time]
    if (!wrapper) {
      return
    }
    const { appBar: { height: appBarHeight } } = this.props.muiTheme
    const scrollPadding = 20
    window.scrollTo(0, wrapper.ref.offsetTop - appBarHeight - scrollPadding)
  }
}

class TrackLine extends React.Component {
  scheduleRefs = {}

  render() {
    const { schedules, muiTheme, preferredLocales } = this.props
    return (
      <div className="ProgramPage-programContainer">
        {schedules.map(schedule => (
          <div key={schedule.date}>
            <DayScheduleTable
              ref={this.onRef(schedule)}
              schedule={schedule}
              preferredLocales={preferredLocales}
              muiTheme={muiTheme}
            />
          </div>
        ))}
      </div>
    )
  }

  onRef = schedule => ref => {
    this.scheduleRefs[schedule.date] = { schedule, ref }
  }

  scrollToScheduleTime() {
    if (this.props.history.action === 'POP') {
      // dont handle back button
      return
    }
    if ('scrollRestoration' in window.history) {
      // enable manual scroll restoration for the moment of custom scrolling
      window.history.scrollRestoration = 'manual'
      setTimeout(() => {
        window.history.scrollRestoration = 'auto'
      }, 500)
    }
    const now = currentDate()
    const current = toLocalDateString(now)
    const wrapper = this.scheduleRefs[current]
    if (wrapper) {
      const currentTime = toLocalTimeString(now)
      const times = wrapper.schedule.timeRows
        .map(r => r.time)
        .concat([currentTime])
      times.sort(byTimesAsc)
      const timeIndex = times.lastIndexOf(currentTime) - 1
      if (timeIndex >= 0) {
        const closestTime = times[timeIndex]
        wrapper.ref.scrollToTime(closestTime)
        return
      }
    }
    // scroll to top otherwise
    window.scrollTo(0, 0)
  }

  componentDidMount() {
    this.scrollToScheduleTime()
  }

  componentDidUpdate(prevProps) {
    if (this.props.location !== prevProps.location) {
      this.scrollToScheduleTime()
    }
  }
}

const programContent = getProgramContent(content)

const withPageContent = withProps(ownProps => {
  const { tracks } = ownProps
  const sessions = getSessions(content)

  const sessionsByDate = _.sortBy(_.toPairs(_.groupBy(sessions, 'date')))
  sessionsByDate.sort((pair1, pair2) => byDatesAsc(pair1[0], pair2[0]))
  const schedules = sessionsByDate.map(([date, sessionsForDay]) => {
    const sessionsByTime = _.sortBy(
      _.toPairs(_.groupBy(sessionsForDay, 'time'))
    )
    sessionsByTime.sort((pair1, pair2) => byTimesAsc(pair1[0], pair2[0]))
    const timeRows = sessionsByTime.map(([time, sessionsForTime]) => {
      const sessionByTrack = sessionsForTime.some(isSessionCrossTrack)
        ? // Here we don't need track indexing and if there are track
          // sessions, they are probably corrupt data sou could be omitted
          sessionsForTime.filter(isSessionCrossTrack)
        : // Mapping by track so that only the specified tracks were inluded
          // and spaces were created where there is no session for left track
          tracks
            .filter(isTrackNonCrossTrack)
            .map(track => sessionsForTime.find(s => s.track.id === track.id))
      return {
        time,
        sessions: sessionByTrack.some(s => !!s) ? sessionByTrack : [],
      }
    })

    return { date, timeRows }
  })
  return {
    text: programContent.text,
    preferredLocales: getPreferredLocales(content),
    schedules,
    tracks,
  }
})

export default compose(withPageContent, withRouter, muiThemeable())(TrackLine)



// WEBPACK FOOTER //
// ./src/containers/TrackLine.js